'use client';

import React, { useContext, useEffect, useMemo, useRef } from 'react';
import makeCancellable from 'make-cancellable-promise';
import clsx from 'clsx';
import invariant from 'tiny-invariant';
import warning from 'tiny-warning';
import { AnnotationLayer as annotationPDFJSLayer } from 'pdfjs-dist';

import DocumentContext from './DocumentContext';
import PageContext from './PageContext';
import useResolver from './shared/hooks/useResolver';
import { cancelRunningTask } from './shared/utils';

import type { IDownloadManager } from 'pdfjs-dist/types/web/interfaces.js';
import type { Annotations } from './shared/types';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function AnnotationLayer() {
    const documentContext = useContext(DocumentContext);
    const pageContext = useContext(PageContext);

    invariant(pageContext, 'Unable to find Page context.');

    const mergedProps = { ...documentContext, ...pageContext };
    const {
        imageResourcesPath,
        linkService,
        onGetAnnotationsError: onGetAnnotationsErrorProps,
        onGetAnnotationsSuccess: onGetAnnotationsSuccessProps,
        onRenderAnnotationLayerError: onRenderAnnotationLayerErrorProps,
        onRenderAnnotationLayerSuccess: onRenderAnnotationLayerSuccessProps,
        onRenderAnnotationLayerFinish: onRenderAnnotationLayerFinishProps,
        page,
        pdf,
        renderForms,
        rotate,
        scale = 1,
    } = mergedProps;

    invariant(
        pdf,
        'Attempted to load page annotations, but no document was specified. Wrap <Page /> in a <Document /> or pass explicit `pdf` prop.'
    );
    invariant(
        page,
        'Attempted to load page annotations, but no page was specified.'
    );
    invariant(
        linkService,
        'Attempted to load page annotations, but no linkService was specified.'
    );

    const [annotationsState, annotationsDispatch] = useResolver<Annotations>();
    const { value: annotations, error: annotationsError } = annotationsState;
    const layerElement = useRef<HTMLDivElement>(null);

    warning(
        parseInt(
            window
                .getComputedStyle(document.body)
                .getPropertyValue('--react-pdf-annotation-layer'),
            10
        ) === 1,
        'AnnotationLayer styles not found. Read more: https://github.com/wojtekmaj/react-pdf#support-for-annotations'
    );

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function onLoadSuccess() {
        if (!annotations) {
            // Impossible, but TypeScript doesn't know that
            return;
        }

        if (onGetAnnotationsSuccessProps) {
            onGetAnnotationsSuccessProps(annotations);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function onLoadError() {
        if (!annotationsError) {
            // Impossible, but TypeScript doesn't know that
            return;
        }

        warning(false, annotationsError.toString());

        if (onGetAnnotationsErrorProps) {
            onGetAnnotationsErrorProps(annotationsError);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function resetAnnotations() {
        annotationsDispatch({ type: 'RESET' });
    }

    useEffect(resetAnnotations, [annotationsDispatch, page]);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function loadAnnotations() {
        if (!page) {
            return;
        }

        const cancellable = makeCancellable(page.getAnnotations());
        const runningTask = cancellable;

        cancellable.promise
            .then((nextAnnotations) => {
                annotationsDispatch({
                    type: 'RESOLVE',
                    value: nextAnnotations,
                });
            })
            .catch((error) => {
                annotationsDispatch({ type: 'REJECT', error });
            });

        // eslint-disable-next-line consistent-return
        return () => {
            cancelRunningTask(runningTask);
        };
    }

    useEffect(loadAnnotations, [annotationsDispatch, page, renderForms]);

    useEffect(
        () => {
            if (annotations === undefined) {
                return;
            }

            if (annotations === false) {
                onLoadError();
                return;
            }

            onLoadSuccess();
        },
        // Ommitted callbacks so they are not called every time they change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [annotations]
    );

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function onRenderSuccess() {
        if (onRenderAnnotationLayerSuccessProps) {
            onRenderAnnotationLayerSuccessProps();
        }
    }
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function onRenderFinish() {
        if (onRenderAnnotationLayerFinishProps) {
            onRenderAnnotationLayerFinishProps();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function onRenderError(error: unknown) {
        warning(false, `${error}`);

        if (onRenderAnnotationLayerErrorProps) {
            onRenderAnnotationLayerErrorProps(error);
        }
    }

    const viewport = useMemo(
        () => page.getViewport({ scale, rotation: rotate }),
        [page, rotate, scale]
    );

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function renderAnnotationLayer() {
        if (!pdf || !page || !linkService || !annotations) {
            return;
        }

        const { current: layer } = layerElement;

        if (!layer) {
            return;
        }

        const clonedViewport = viewport.clone({ dontFlip: true });

        const annotationLayerParameters = {
            accessibilityManager: null, // TODO: Implement this
            annotationCanvasMap: null, // TODO: Implement this
            div: layer,
            annotationEditorUIManager: null, // TODO: Implement this
            page,
            viewport: clonedViewport,
            structTreeLayer: null, // TODO: Implement this
        };

        const renderParameters = {
            annotations,
            annotationStorage: pdf.annotationStorage,
            div: layer,
            // See https://github.com/mozilla/pdf.js/issues/17029
            downloadManager: null as unknown as IDownloadManager,
            imageResourcesPath,
            linkService,
            page,
            renderForms,
            viewport: clonedViewport,
        };

        layer.innerHTML = '';

        try {
            // eslint-disable-next-line new-cap
            new annotationPDFJSLayer(annotationLayerParameters)
                .render(renderParameters)
                .then(() => {
                    onRenderFinish();
                });

            // Intentional immediate callback
            onRenderSuccess();
        } catch (error) {
            onRenderError(error);
        }

        // eslint-disable-next-line consistent-return
        return () => {
            // TODO: Cancel running task?
        };
    }

    useEffect(
        renderAnnotationLayer,
        // Ommitted callbacks so they are not called every time they change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            annotations,
            imageResourcesPath,
            linkService,
            page,
            renderForms,
            viewport,
        ]
    );

    return (
        <div
            className={clsx('react-pdf__Page__annotations', 'annotationLayer')}
            ref={layerElement}
        />
    );
}
